import { useAuth0 } from '@auth0/auth0-react';
import {
  ArchiveIcon,
  ChartBarIcon,
  CopyIcon,
  EnvelopeSimpleIcon,
  GearIcon,
  GiftIcon,
  GitBranchIcon,
  HouseIcon,
  QuestionIcon,
  SignOutIcon,
  SquaresFourIcon,
  WifiSlashIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useGetBranches } from 'api/dev/branches';
import { FeatureFlags, useGetUserFeatureFlags } from 'api/general/features';
import { sanityClient } from 'api/releaseNotes/sanityClient';
import { clearBranchCookie, getCurrentBranch } from 'features/Developer/utils/branchCookie';
import { BRANCH_DEPLOYMENTS_ENABLED, getBranchDeploymentUrl } from 'features/Developer/utils/branchDeployments';
import { useIsOnline } from 'hooks/useIsOnline';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from 'ui-library/components/Button/Button';
import { Chip } from 'ui-library/components/Chip/Chip';
import { DropdownMenu } from 'ui-library/components/DropdownMenu/DropdownMenu';
import { IconButton } from 'ui-library/components/IconButton/IconButton';
import { NavItem } from 'ui-library/components/NavItem/NavItem';
import { Tooltip } from 'ui-library/components/Tooltip/Tooltip';
import { openSupportEmail } from 'utils/contactSupport';
import { developerTools, legacyWorkflows } from 'utils/roles/groupings';
import { useRolesCheck } from 'utils/roles/useRolesCheck';
import { getInitials } from 'utils/string';
import { PATHNAMES } from '../../constants/constants';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { resetReduxState } from '../../utils/reduxReset';
import { SharedWithMeDropdown } from '../SharedWithMeDropdown/SharedWithMeDropdown';
import s from './Header.module.scss';

export const Header = () => {
  'use memo';

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isOnline = useIsOnline();
  const { isAuthenticated, logout } = useAuth0();
  const { isDeveloper, canAccessWorkflow } = useRolesCheck();
  const authUser = useAppSelector(store => store.authSlice.user);
  const { data: featureFlags } = useGetUserFeatureFlags();

  const accountInitials = authUser ? getInitials(authUser) : undefined;

  const accessibleLegacyWorkflows = legacyWorkflows.filter(w => canAccessWorkflow(w.type));
  const accessibleDeveloperTools = developerTools.filter(w => canAccessWorkflow(w.type));

  const cookieBranch = BRANCH_DEPLOYMENTS_ENABLED && isDeveloper ? getCurrentBranch() : null;
  const [staleRemoved, setStaleRemoved] = useState(false);
  const currentBranch = staleRemoved ? null : cookieBranch;

  // The cookie can outlive the branch's assets (e.g. retentioned from S3). Validate it against
  // the live branch list on load and drop it if the branch no longer exists.
  useGetBranches({
    enabled: !!cookieBranch,
    onSuccess: branches => {
      if (cookieBranch && !branches.includes(cookieBranch)) {
        clearBranchCookie();
        setStaleRemoved(true);
        enqueueSnackbar(`Branch "${cookieBranch}" no longer exists and has been removed.`, { variant: 'warning' });
      }
    },
  });

  const handleExitBranch = () => {
    clearBranchCookie();
    window.location.reload();
  };

  const handleCopyBranchUrl = () => {
    if (!currentBranch) return;
    void navigator.clipboard.writeText(getBranchDeploymentUrl(currentBranch));
    enqueueSnackbar('Branch URL copied to clipboard', { variant: 'success' });
  };

  const handleSignOut = () => {
    resetReduxState(dispatch);
    void logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <header id="header" className={s.header}>
      <div className={s.headerNavPart}>
        <Link className={s.headerLogo} to={PATHNAMES.main} onClick={() => resetReduxState(dispatch)}>
          <img src="/images/logo.svg" alt="ANKAR AI" width={89} />
        </Link>

        <nav aria-label="Primary" className={s.headerNavItems}>
          <NavItem to={PATHNAMES.main} end icon={<HouseIcon />} label="Home" />
          <NavItem to={PATHNAMES.allProjects} icon={<SquaresFourIcon />} label="All projects" />
          {featureFlags?.[FeatureFlags.PROJECTS_ANALYTICS] && (
            <NavItem to={PATHNAMES.analytics} icon={<ChartBarIcon />} label="Analytics" />
          )}
          {accessibleLegacyWorkflows.length > 0 && (
            <DropdownMenu trigger={<NavItem icon={<ArchiveIcon />} label="Other modules" />}>
              {accessibleLegacyWorkflows.map(workflow => (
                <DropdownMenu.Item
                  key={workflow.link}
                  onSelect={() => {
                    resetReduxState(dispatch);
                    void navigate(workflow.link);
                  }}
                >
                  {workflow.title}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          )}
        </nav>
      </div>

      <div className={s.headerButtons}>
        {!isOnline && (
          <Chip
            colour="red"
            size="s"
            leadingIcon={<WifiSlashIcon size={14} weight="bold" />}
            label="Lost connection - trying to reconnect. Please check your network"
          />
        )}
        {currentBranch ? (
          <DropdownMenu
            trigger={
              <span>
                <Tooltip title="Branch deployment" side="bottom">
                  <Chip
                    colour="yellow"
                    size="s"
                    leadingIcon={<GitBranchIcon size={14} weight="bold" />}
                    label={currentBranch}
                  />
                </Tooltip>
              </span>
            }
            align="end"
          >
            <DropdownMenu.Item icon={<CopyIcon size={16} />} onSelect={handleCopyBranchUrl}>
              {currentBranch}
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item icon={<XCircleIcon size={16} />} variant="danger" onSelect={handleExitBranch}>
              Exit branch
            </DropdownMenu.Item>
          </DropdownMenu>
        ) : undefined}
        {sanityClient ? (
          <Tooltip title="Release Notes">
            <IconButton
              variant="clear"
              size="md"
              className={s.headerIconButton}
              title="Release Notes"
              onClick={() => void navigate(PATHNAMES.releaseNotes)}
            >
              <GiftIcon />
            </IconButton>
          </Tooltip>
        ) : undefined}
        <DropdownMenu
          trigger={
            <span>
              <Tooltip title="Help" side="bottom">
                <IconButton variant="clear" size="sm" title="Help" className={s.headerIconButton}>
                  <QuestionIcon />
                </IconButton>
              </Tooltip>
            </span>
          }
          align="end"
        >
          <DropdownMenu.Item icon={<EnvelopeSimpleIcon size={16} />} onSelect={() => openSupportEmail()}>
            Contact Support
          </DropdownMenu.Item>
        </DropdownMenu>
        <SharedWithMeDropdown triggerClassName={s.headerIconButton} />
        {accessibleDeveloperTools.length > 0 && (
          <DropdownMenu
            trigger={
              <span>
                <IconButton variant="clear" size="sm" title="Developer Tools" className={s.headerIconButton}>
                  <GearIcon />
                </IconButton>
              </span>
            }
            align="end"
          >
            {accessibleDeveloperTools.map(tool => (
              <DropdownMenu.Item
                key={tool.link}
                onSelect={() => {
                  resetReduxState(dispatch);
                  void navigate(tool.link);
                }}
              >
                {tool.title}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        )}

        {isAuthenticated ? (
          <DropdownMenu tooltip="Account" trigger={<button className={s.headerAvatar}>{accountInitials}</button>}>
            <DropdownMenu.Item icon={<SignOutIcon size={16} />} variant="danger" onSelect={handleSignOut}>
              Sign out
            </DropdownMenu.Item>
          </DropdownMenu>
        ) : (
          <Button size="sm" variant="primary" onClick={() => void navigate(PATHNAMES.authLogin)}>
            Log in
          </Button>
        )}
      </div>
    </header>
  );
};
